import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { History, Check, Download, FileText, FileUp, Loader2, Pencil, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, download, errMsg } from "@/lib/api";
import { Empty, Loading, PageHeader, StatusPill } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CareerProfiles from "@/components/CareerProfiles";
import FactDetails from "@/components/FactDetails";
const TYPES = ["experience", "skill", "achievement", "education", "certification", "industry", "summary", "contact"];
function TypeSelect({
  value,
  onChange,
  testId
}) {
  return <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-40" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
    </Select>;
}
function FactRow({
  f,
  onChange,
  selected,
  onSelect
}) {
  const [history, setHistory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(f.text);
  const [type, setType] = useState(f.type);
  const [years, setYears] = useState(f.data?.years ?? "");
  const [details, setDetails] = useState(f.data || {});
  const [editVersion, setEditVersion] = useState(f.version || 0);
  const patch = async (body, version = f.version || 0) => {
    setSaving(true);
    try {
      onChange((await api.patch(`/facts/${f.id}`, {
        ...body,
        version
      })).data);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    const data = {
      ...details
    };
    if (type === "experience") data.years = years === "" ? undefined : Number(years);
    if (await patch({
      text,
      type,
      data,
      status: "confirmed"
    }, editVersion)) setEdit(false);
  };
  return <li data-testid={`fact-row-${f.id}`} className="flex flex-col gap-3 border-b px-5 py-4 last:border-0 sm:flex-row sm:items-start">
      <input type="checkbox" aria-label={`Select fact: ${f.text}`} checked={selected} onChange={onSelect} className="mt-1 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow">{f.type}</span>
          <StatusPill status={f.status} testId={`fact-status-${f.id}`} />
          {f.edited && <span className="text-[11px] text-muted-foreground">edited</span>}
          <span className="text-[11px] text-muted-foreground">from {f.source}</span>
        </div>
        {edit ? <div className="space-y-2">
            <div className="flex flex-wrap gap-2"><TypeSelect value={type} onChange={setType} testId={`fact-type-${f.id}`} />
              {type === "experience" && <Input className="w-32" type="number" step="0.5" min="0" max="80" placeholder="Years" aria-label="Years in role" value={years} onChange={e => setYears(e.target.value)} data-testid={`fact-years-${f.id}`} />}</div>
            <Textarea value={text} onChange={e => setText(e.target.value)} data-testid={`fact-text-input-${f.id}`} />
            <FactDetails type={type} value={details} onChange={setDetails} id={`fact-${f.id}`} />
          </div> : <p className="break-words text-sm" data-testid={`fact-text-${f.id}`}>{f.text}{f.data?.years ? <span className="text-muted-foreground"> · {f.data.years} yrs</span> : null}</p>}
      </div>
      <div className="flex shrink-0 flex-wrap gap-1.5">
        {edit ? <><Button size="sm" disabled={saving} onClick={save} data-testid={`fact-save-${f.id}`}>Save & confirm</Button>
            <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>Cancel</Button></> : <>
            <Button size="icon" variant="ghost" aria-label="Fact edit history" onClick={async () => {
          try {
            setHistory((await api.get(`/facts/${f.id}/history`)).data);
          } catch (e) {
            toast.error(errMsg(e));
          }
        }}><History size={15} /></Button>
            {f.status !== "confirmed" && <Button size="sm" variant="outline" disabled={saving} onClick={() => patch({
          status: "confirmed"
        })} data-testid={`fact-confirm-${f.id}`}><Check className="mr-1 h-3.5 w-3.5" />Confirm</Button>}
            <Button size="icon" variant="ghost" aria-label="Edit fact" onClick={() => {setText(f.text);setType(f.type);setYears(f.data?.years ?? "");setDetails(f.data || {});setEditVersion(f.version || 0);setEdit(true);}} data-testid={`fact-edit-${f.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" aria-label="Reject fact" disabled={saving} onClick={() => patch({
          status: "rejected"
        })} data-testid={`fact-reject-${f.id}`}><X className="h-4 w-4" /></Button>
          </>}
      </div>
      <Dialog open={!!history} onOpenChange={v => {
      if (!v) setHistory(null);
    }}><DialogContent className="max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Fact edit history</DialogTitle><DialogDescription>Previous versions are retained for your review (latest 100 edits). Earlier edits may predate history tracking.</DialogDescription></DialogHeader><p className="text-sm">Current: {history?.current.text}</p>{history?.revisions.map((r, i) => <div key={i} className="border-t pt-3"><p className="text-xs text-muted-foreground">Version {r.version} · {r.status} · {new Date(r.replaced_at).toLocaleString()}</p><p className="mt-2 text-sm break-words">{r.text}</p></div>)}</DialogContent></Dialog>
    </li>;
}
function AddFact({
  onAdded
}) {
  const [type, setType] = useState("skill");
  const [text, setText] = useState("");
  const [years, setYears] = useState("");
  const [details, setDetails] = useState({});
  const add = async e => {
    e.preventDefault();
    try {
      const data = {...details, ...(type === "experience" && years ? {years:Number(years)} : {})};
      onAdded((await api.post("/facts", {
        type,
        text,
        data
      })).data);
      setText("");
      setYears("");
      setDetails({});
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return <form onSubmit={add} className="flex flex-wrap gap-2 border-t p-4" data-testid="add-fact-form">
      <TypeSelect value={type} onChange={setType} testId="add-fact-type" />
      {type === "experience" && <Input className="sm:w-28" type="number" step="0.5" min="0" max="80" placeholder="Years" aria-label="Years" value={years} onChange={e => setYears(e.target.value)} data-testid="add-fact-years" />}
      <Input required className="min-w-0 flex-1 basis-64" placeholder="Add a fact you can stand behind" value={text} onChange={e => setText(e.target.value)} data-testid="add-fact-text" />
      <Button data-testid="add-fact-submit"><Plus className="mr-1 h-4 w-4" />Add</Button>
      <FactDetails type={type} value={details} onChange={setDetails} id="new-fact" />
    </form>;
}
export default function Profile() {
  const [facts, setFacts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [selected, setSelected] = useState([]);
  const [review, setReview] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const fileRef = useRef();
  const load = () => {
    setError("");
    return Promise.all([api.get("/facts?include_rejected=true"), api.get("/resumes")]).then(([f, r]) => {
      setFacts(f.data);
      setResumes(r.data);
      setSelected([]);
    }).catch(e => setError(errMsg(e)));
  };
  useEffect(() => {
    load();
  }, []);
  const upload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const {
        data
      } = await api.post("/resume/upload", fd);
      toast.success(`${data.count} facts extracted (${data.method === "ai" ? "AI" : "basic"} extraction). Review each one.`);
      if (data.warning) toast.warning(data.warning);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const update = nf => setFacts(fs => fs.map(x => x.id === nf.id ? nf : x));
  const reviewSelected = async () => {
    setBusy(true);
    const ids = review.ids;
    try {
      const versions = Object.fromEntries(facts.filter(f => ids.includes(f.id)).map(f => [f.id, f.version || 0]));
      const r = await api.post("/facts/bulk", {
        ids,
        versions,
        status: review.status
      });
      toast.success(`${r.data.updated} facts updated`);
      if (r.data.conflicts.length) toast.warning("Some facts changed elsewhere. Review them again.");
      setReview(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const unconfirmed = facts?.filter(f => f.status === "unconfirmed").length || 0;
  const sorted = facts ? facts.filter(f => (filter === "all" || f.type === filter) && (status === "all" || (status === "active" ? f.status !== "rejected" : f.status === status)) && `${f.text} ${f.source}`.toLowerCase().includes(query.toLowerCase())).sort((a, b) => a.status === b.status ? 0 : a.status === "unconfirmed" ? -1 : 1) : [];
  return <div>
      <PageHeader eyebrow="Verified career profile" title="Your career, backed by facts" testId="profile-heading">
        <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={upload} data-testid="resume-file-input" />
        <Button onClick={() => fileRef.current.click()} disabled={busy} data-testid="resume-upload-button">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}Import resume (PDF/DOCX)</Button>
      </PageHeader>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3"><p className="max-w-xl text-sm leading-relaxed text-muted-foreground">Your experience is the source of truth. Confirm what is accurate, correct what is missing and build documents from facts you trust.</p><div className="flex gap-2">{["pdf", "docx"].map(format => <Button key={format} variant="outline" size="sm" disabled={!facts?.some(f => f.status === "confirmed")} onClick={() => download(`/me/cv?format=${format}`, `career-profile.${format}`).catch(e => toast.error(errMsg(e)))}><Download size={14} />CV {format.toUpperCase()}</Button>)}</div></div>
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <div className="panel p-5"><p className="eyebrow">Confirmed</p><p className="mt-1 font-display text-3xl font-bold" data-testid="facts-confirmed-count">{facts ? facts.filter(f => f.status === "confirmed").length : "—"}</p></div>
        <div className="panel p-5"><p className="eyebrow">Awaiting your review</p><p className="mt-1 font-display text-3xl font-bold text-warn" data-testid="facts-unconfirmed-count">{unconfirmed}</p></div>
        <div className="panel p-5 text-sm text-muted-foreground">Generated documents only use <strong className="text-foreground">confirmed</strong> facts. Add years to experience facts to improve experience scoring.</div>
      </div>
      {error && <p role="alert" className="mb-4 panel p-4 text-sm text-destructive">{error}</p>}
      <CareerProfiles facts={facts} selected={selected} />
      <div className="mb-4 flex flex-wrap gap-2" aria-label="Filter career facts">{["all", ...TYPES].map(type => <button key={type} aria-pressed={filter === type} onClick={() => setFilter(type)} className={`rounded-lg border px-3 py-2 text-xs capitalize ${filter === type ? "border-primary bg-primary/10 text-primary" : "bg-card text-muted-foreground"}`}>{type === "all" ? "All facts" : type}</button>)}</div>
      <div className="mb-4 flex flex-wrap gap-3"><Input aria-label="Search career facts" placeholder="Search facts or sources…" value={query} onChange={e => setQuery(e.target.value)} className="sm:max-w-sm" /><select aria-label="Fact status" className="h-10 rounded-md border bg-background px-3 text-sm" value={status} onChange={e => setStatus(e.target.value)}>{["active", "unconfirmed", "confirmed", "rejected", "all"].map(s => <option key={s} value={s}>{s}</option>)}</select><Button variant="outline" size="sm" onClick={() => setSelected(sorted.map(f => f.id))}>Select visible ({sorted.length})</Button>{selected.length > 0 && <><Button size="sm" onClick={() => setReview({
          ids: selected,
          status: "confirmed"
        })}>Confirm selected ({selected.length})</Button><Button size="sm" variant="outline" onClick={() => setReview({
          ids: selected,
          status: "rejected"
        })}>Reject selected</Button><Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button></>}</div>
      {!facts ? !error && <Loading /> : <div className="panel overflow-hidden">
          {unconfirmed > 0 && <div className="flex items-center justify-between gap-3 border-b bg-amber-500/5 px-5 py-3 text-sm">
              <span>{unconfirmed} extracted facts need a decision.</span>
              <Button size="sm" variant="outline" onClick={() => setReview({
          ids: facts.filter(f => f.status === "unconfirmed").map(f => f.id),
          status: "confirmed"
        })} data-testid="confirm-all-facts">Confirm all as accurate</Button>
            </div>}
          {sorted.length === 0 ? <div className="p-5"><Empty icon={FileText} title="No facts yet" testId="facts-empty">Import a resume or add facts manually below.</Empty></div> : <ul data-testid="facts-list">{sorted.map(f => <FactRow key={f.id} f={f} onChange={update} selected={selected.includes(f.id)} onSelect={() => setSelected(ids => ids.includes(f.id) ? ids.filter(id => id !== f.id) : [...ids, f.id])} />)}</ul>}
          <AddFact onAdded={f => setFacts(fs => [...fs, f])} />
        </div>}
      <Dialog open={!!review} onOpenChange={v => {
      if (!v && !busy) setReview(null);
    }}><DialogContent><DialogHeader><DialogTitle>Review {review?.ids.length} career facts</DialogTitle><DialogDescription>{review?.status === "confirmed" ? "Confirm only facts you have checked for accuracy. These can be used in generated documents." : "Rejected facts stop being used in documents. You can restore them from the rejected view."}</DialogDescription></DialogHeader><div className="flex flex-wrap gap-2"><Button disabled={busy} onClick={reviewSelected}>{busy ? "Saving…" : review?.status === "confirmed" ? "I have checked these facts" : "Reject these facts"}</Button><Button variant="outline" disabled={busy} onClick={() => setReview(null)}>Cancel</Button></div></DialogContent></Dialog>
      <section className="panel mt-6 p-5"><h2 className="section-title">Resume import history</h2><p className="mt-1 text-xs text-muted-foreground">Imported facts need your review before use.</p>{resumes.map(r => <div key={r.id} className="flex flex-wrap justify-between gap-2 border-b py-3 text-xs last:border-0"><span className="break-all">{r.filename}</span><span className="text-muted-foreground">{r.fact_count} facts · {r.method} · {new Date(r.uploaded_at).toLocaleDateString()}</span></div>)}{!resumes.length && <p className="mt-4 text-xs text-muted-foreground">No resumes imported yet.</p>}</section>
    </div>;
}
